import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { BottomButtonsLayout, Button, Shadow, theme } from 'aimo-ui';
import SelectVehiclesModalContent from 'components/park/SelectVehiclesModalContent';
import AccountContext from 'context/AccountContext';
import useShadow from 'hooks/useShadow';
import {
  licensePlatePermissionScreenOptionsForMobile,
  licensePlatePermissionScreenOptionsForWeb,
  modalFullScreenOptions,
  modalPartialScreenOptions,
} from 'navigation/DifferentModalViews';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContentWrapper from 'screens/permit/ModalContentWrapper';
import { usePermitStore } from 'stores/permitStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { LicensePlate, UnifiedPermit } from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { handlePermitNavigation } from 'utils/permitUtils';
import { VehicleSelectPermission } from './VehicleSelectPermission';

const PermitSelectVehicle: React.FC = () => {
  const { t } = useTranslation();
  const { popTo: navigate } = useNavigation<RootNavProps>();
  const {
    params: { permitId },
  } = useRoute<RouteProp<RootStackParamList, 'permitVehicles'>>();
  const [selectedLicensePlates, setSelectedLicensePlates] = React.useState<
    LicensePlate[]
  >(usePermitStore.getState().selectedLicensePlatesPermitRedeem);
  const [isVehicleSelected, setIsVehicleSelected] = useState(false);

  const {
    customer: { permits },
  } = useContext(AccountContext);
  let permit = permits?.find((p) => p?.id === permitId) as UnifiedPermit;

  if (!permit) {
    permit = permits?.find(
      (p) => p?.orderLineIdWithVersion === permitId
    ) as UnifiedPermit;
  }
  const orderLineIdWithVersion = permit?.orderLineIdWithVersion as string;

  const allowOneVehicle =
    !!permit?.maxLicensePlates && permit?.maxLicensePlates === 1;

  const { setOptions } = useNavigation();

  const licensePlateIdentificationPermissionScreenMustBeShown =
    permit.identifyCustomerFromPermit && isVehicleSelected;

  const setNavigationToPartialScreen = (setOptions: Function) => {
    setOptions({
      ...modalPartialScreenOptions.screenOptions,
      cardStyle: isWeb
        ? licensePlatePermissionScreenOptionsForWeb.cardStyle
        : licensePlatePermissionScreenOptionsForMobile.cardStyle,
    });
  };

  const setNavigationToFullScreen = (setOptions: Function) => {
    setOptions({
      ...modalFullScreenOptions.screenOptions,
      cardStyle: isWeb
        ? modalFullScreenOptions.cardStyle
        : modalPartialScreenOptions.cardStyle,
    });
  };

  useEffect(() => {
    if (licensePlateIdentificationPermissionScreenMustBeShown) {
      setNavigationToPartialScreen(setOptions);
    } else {
      setNavigationToFullScreen(setOptions);
    }
  }, [permit.identifyCustomerFromPermit, isVehicleSelected, setOptions]);

  const { defaultShadowDistance } = useShadow();

  const commitPlatesAndNavigate = (plates: LicensePlate[]) => {
    usePermitStore
      .getState()
      .setSelectedLicensePlatesPermitRedeem(
        plates.map(({ __typename, ...rest }) => ({ ...rest }))
      );

    if (orderLineIdWithVersion) {
      navigate('salesOrderPermitEdit', { orderLineIdWithVersion });
    } else {
      handlePermitNavigation(permit, navigate);
    }
  };

  const handleSave = () => {
    commitPlatesAndNavigate(selectedLicensePlates);
  };

  const handleOnChange = (licensePlates: LicensePlate[]) => {
    setIsVehicleSelected(true);
    setSelectedLicensePlates(licensePlates);
    if (permit.maxLicensePlates === 1 && !permit.identifyCustomerFromPermit) {
      commitPlatesAndNavigate(licensePlates);
    }
  };

  const handleContinue = () => {
    handleSave();
    setIsVehicleSelected(false);
  };

  const handleCancel = () => {
    setIsVehicleSelected(false);
    setSelectedLicensePlates([]);
    navigate('permit', { permitId });
  };

  return (
    <BottomButtonsLayout
      contentBottom={
        !allowOneVehicle && (
          <Shadow shadowDistance={defaultShadowDistance}>
            <ColumnWithMargins
              mr={20}
              ml={20}
              mt={20}
              mb={theme.normalising.heightPixel(30)}>
              <Button
                size="l"
                onPress={handleSave}
                text={t('button.save')}
                testID="redeem-permit-button"
              />
            </ColumnWithMargins>
          </Shadow>
        )
      }>
      <ModalContentWrapper>
        {licensePlateIdentificationPermissionScreenMustBeShown ? (
          <VehicleSelectPermission
            handleContinue={handleContinue}
            handleCancel={handleCancel}
          />
        ) : (
          <SelectVehiclesModalContent
            maxLicensePlates={permit.maxLicensePlates}
            handleOnChange={handleOnChange}
          />
        )}
      </ModalContentWrapper>
    </BottomButtonsLayout>
  );
};

export default PermitSelectVehicle;
